import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import api from '../api/client';
import { useTheme } from '../context/ThemeContext';
import './MySubscription.css';

// Get the main platform URL based on environment
const getMainPlatformUrl = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000';
  }
  return 'https://ai.app.platform.halisoft.biz';
};

// Module-specific configurations (display only; pricing comes from GET /api/subscriptions/plans)
const MODULE_CONFIGS = {
  'risk-analysis': {
    name: 'HaliCredit',
    title: 'Credit Risk Assessment',
    description: 'Analyze debtor creditworthiness, payment history and financial health to assess invoice financing risk',
    icon: '📊',
    color: '#f43f5e',
    launchPath: '/risk-analysis', // Path within customer-portal
    launchLabel: 'Launch Risk Analysis',
    isInternal: true, // Navigate within customer-portal, not external
    features: [
      'AI-powered risk scoring',
      'Credit analysis reports',
      'Fraud detection alerts',
      'Real-time monitoring'
    ]
  },
  'hali-leads': {
    name: 'HaliLeads',
    title: 'Leads Agent',
    description: 'AI lead qualification and scoring for sales teams',
    icon: '🎯',
    color: '#8b5cf6',
    launchPath: '/hali-leads', // Path within customer-portal
    launchLabel: 'Launch Leads Agent',
    isInternal: true, // Navigate within customer-portal, not external
    features: [
      'Lead qualification AI',
      'Scoring automation',
      'CRM integration',
      'Pipeline analytics'
    ]
  },
  'contract-analyzer': {
    name: 'HaliContract',
    title: 'Contract Analyzer Agent',
    description: 'AI-powered contract analysis and clause extraction for trade finance',
    icon: '📜',
    color: '#10b981',
    launchPath: '/contract-analyzer', // Path within customer-portal
    launchLabel: 'Launch Contract Analyzer',
    isInternal: true, // Navigate within customer-portal, not external
    features: [
      'AI contract analysis',
      'Clause extraction',
      'Risk detection',
      'Compliance checks'
    ]
  },
  'invoice-financing-check': {
    name: 'HaliVerify',
    title: 'Invoice Eligibility Checker',
    description: 'AI-powered verification of invoice authenticity and financing eligibility assessment',
    icon: '📋',
    color: '#6366f1',
    launchPath: '/invoice-financing', // Path within customer-portal
    launchLabel: 'Launch Invoice Check',
    isInternal: true, // Navigate within customer-portal, not external
    features: [
      'Invoice verification',
      'Eligibility analysis',
      'Risk assessment',
      'Fraud detection'
    ]
  },
  'past-trade-studio': {
    name: 'HaliTrade',
    title: 'Past Trade Studio',
    description: 'AI-powered trade document analysis with risk detection, pattern analysis, and comprehensive audit trails',
    icon: '📊',
    color: '#f59e0b',
    launchPath: '/past-trade-studio', // Path within customer-portal
    launchLabel: 'Launch Past Trade Studio',
    isInternal: true, // Navigate within customer-portal, not external
    features: [
      'Trade document analysis',
      'Risk detection',
      'Pattern analysis',
      'Audit trail management'
    ]
  }
};

const MySubscription = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isDark } = useTheme();
  const moduleId = searchParams.get('module'); // e.g., 'risk-analysis' or 'hali-leads'
  const moduleConfig = moduleId ? MODULE_CONFIGS[moduleId] : null;
  const pricingRef = useRef(null);

  const [user, setUser] = useState(null);
  const [quota, setQuota] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [canceling, setCanceling] = useState(false);

  // Module-specific data for the "My AI Tools" cards
  const [riskData, setRiskData] = useState({ subscription: null, quota: null });
  const [leadsData, setLeadsData] = useState({ subscription: null, quota: null });
  const [contractData, setContractData] = useState({ subscription: null, quota: null });
  const [invoiceFinancingData, setInvoiceFinancingData] = useState({ subscription: null, quota: null });
  const [pastTradeData, setPastTradeData] = useState({ subscription: null, quota: null });

  // Theme-aware colors
  const colors = {
    cardBg: isDark ? '#1e293b' : 'white',
    textPrimary: isDark ? '#f3f4f6' : '#1f2937',
    textSecondary: isDark ? '#9ca3af' : '#6b7280',
    textMuted: isDark ? '#6b7280' : '#4b5563',
    border: isDark ? '#334155' : '#e5e7eb',
    bgMuted: isDark ? '#0f172a' : '#f3f4f6',
    sectionBg: isDark ? 'linear-gradient(135deg, #7c3aed10, #7c3aed05)' : 'linear-gradient(135deg, #7c3aed20, #7c3aed05)'
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get email from URL first, then localStorage
      const urlParams = new URLSearchParams(window.location.search);
      let storedEmail = urlParams.get('email');
      
      if (!storedEmail) {
        storedEmail = localStorage.getItem('halisoft_email');
      }
      if (!storedEmail) {
        const storedUser = localStorage.getItem('halisoft_user');
        if (storedUser) {
          try {
            storedEmail = JSON.parse(storedUser)?.email;
          } catch (e) { /* ignore */ }
        }
      }
      // Always store for future use
      if (storedEmail) {
        localStorage.setItem('halisoft_email', storedEmail);
      }
      console.log('[MySubscription] Using email:', storedEmail);
      
      // Determine which module we're viewing (if any)
      const currentModule = moduleId || 'global';
      const moduleParam = currentModule !== 'global' ? `&module=${currentModule}` : '';
      
      const [userRes, plansRes, currentSubRes] = await Promise.all([
        api.get('/api/check-auth').catch(() => ({ data: { user: storedEmail ? { email: storedEmail } : null } })),
        // IMPORTANT: module-aware plans so admin updates show up per product
        api.get('/api/subscriptions/plans', { params: moduleId ? { module: moduleId } : {} }).catch(() => ({ data: [] })),
        storedEmail 
          ? api.get(`/api/subscriptions/current?email=${storedEmail}${moduleParam}`).catch(() => ({ data: null }))
          : api.get(`/api/subscriptions/current?${moduleParam.substring(1)}`).catch(() => ({ data: null }))
      ]);

      // Get quota with email and module parameter
      const quotaRes = storedEmail 
        ? await api.get(`/api/user/quota?email=${storedEmail}${moduleParam}`).catch(() => ({ data: { used: 0, limit: 100, remaining: 100 } }))
        : await api.get(`/api/user/quota?${moduleParam.substring(1)}`).catch(() => ({ data: { used: 0, limit: 100, remaining: 100 } }));

      console.log('[MySubscription] API responses:', {
        plans: plansRes.data,
        currentSub: currentSubRes.data,
        quota: quotaRes.data
      });
      
      setQuota(quotaRes.data);
      setPlans(plansRes.data);
      
      // Set user with current subscription
      const userData = userRes.data?.user || { email: storedEmail || 'Anonymous' };
      // Check if we have a valid subscription (not free fallback)
      if (currentSubRes.data && currentSubRes.data.plan_id && currentSubRes.data.plan_id !== 'free') {
        userData.subscription = {
          plan: currentSubRes.data,
          status: currentSubRes.data.status || 'active'
        };
        console.log('[MySubscription] Set subscription:', userData.subscription);
      } else {
        console.log('[MySubscription] No active subscription, using free');
      }
      setUser(userData);
      
      // Load module-specific data for "My AI Tools" cards (always fetch all)
      if (storedEmail) {
        const [riskSubRes, riskQuotaRes, leadsSubRes, leadsQuotaRes, contractSubRes, contractQuotaRes, invoiceSubRes, invoiceQuotaRes, pastTradeSubRes, pastTradeQuotaRes] = await Promise.all([
          api.get(`/api/subscriptions/current?email=${storedEmail}&module=risk-analysis`).catch(() => ({ data: null })),
          api.get(`/api/user/quota?email=${storedEmail}&module=risk-analysis`).catch(() => ({ data: { used: 0, limit: 10, remaining: 10, plan: 'Free' } })),
          api.get(`/api/subscriptions/current?email=${storedEmail}&module=hali-leads`).catch(() => ({ data: null })),
          api.get(`/api/user/quota?email=${storedEmail}&module=hali-leads`).catch(() => ({ data: { used: 0, limit: 20, remaining: 20, plan: 'Free' } })),
          api.get(`/api/subscriptions/current?email=${storedEmail}&module=contract-analyzer`).catch(() => ({ data: null })),
          api.get(`/api/user/quota?email=${storedEmail}&module=contract-analyzer`).catch(() => ({ data: { used: 0, limit: 5, remaining: 5, plan: 'Free' } })),
          api.get(`/api/subscriptions/current?email=${storedEmail}&module=invoice-financing-check`).catch(() => ({ data: null })),
          api.get(`/api/user/quota?email=${storedEmail}&module=invoice-financing-check`).catch(() => ({ data: { used: 0, limit: 10, remaining: 10, plan: 'Free' } })),
          api.get(`/api/subscriptions/current?email=${storedEmail}&module=past-trade-studio`).catch(() => ({ data: null })),
          api.get(`/api/user/quota?email=${storedEmail}&module=past-trade-studio`).catch(() => ({ data: { used: 0, limit: 5, remaining: 5, plan: 'Free' } }))
        ]);

        setRiskData({
          subscription: riskSubRes.data,
          quota: riskQuotaRes.data
        });
        setLeadsData({
          subscription: leadsSubRes.data,
          quota: leadsQuotaRes.data
        });
        setContractData({
          subscription: contractSubRes.data,
          quota: contractQuotaRes.data
        });
        setInvoiceFinancingData({
          subscription: invoiceSubRes.data,
          quota: invoiceQuotaRes.data
        });
        setPastTradeData({
          subscription: pastTradeSubRes.data,
          quota: pastTradeQuotaRes.data
        });

        console.log('[MySubscription] Module data loaded:', {
          risk: { sub: riskSubRes.data?.name, quota: riskQuotaRes.data?.limit },
          leads: { sub: leadsSubRes.data?.name, quota: leadsQuotaRes.data?.limit },
          contract: { sub: contractSubRes.data?.name, quota: contractQuotaRes.data?.limit },
          invoiceFinancing: { sub: invoiceSubRes.data?.name, quota: invoiceQuotaRes.data?.limit }
        });
      }
    } catch (err) {
      console.error('Error loading data:', err);
      if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Error loading data');
      }
    } finally {
      setLoading(false);
    }
  }, [moduleId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle payment return (Stripe session_id or PayPal token)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const token = params.get('token');
    const cancelled = params.get('cancelled');

    if (cancelled === '1') {
      setError('Payment was cancelled. You can try again when ready.');
      window.history.replaceState({}, '', window.location.pathname + (params.get('module') ? `?module=${params.get('module')}` : ''));
      return;
    }

    if (sessionId) {
      (async () => {
        try {
          setLoading(true);
          const { data } = await api.post('/api/subscriptions/stripe/verify-session', { sessionId });
          if (data?.success) {
            setError('');
            window.history.replaceState({}, '', window.location.pathname);
            await loadData();
          } else {
            setError('Payment verification failed. Please contact support if you were charged.');
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Payment verification failed.');
        } finally {
          setLoading(false);
        }
      })();
    } else if (token) {
      (async () => {
        try {
          setLoading(true);
          const { data } = await api.post('/api/subscriptions/paypal/capture-order', { orderId: token });
          if (data?.success) {
            setError('');
            window.history.replaceState({}, '', window.location.pathname);
            await loadData();
          } else {
            setError('Payment capture failed. Please contact support.');
          }
        } catch (err) {
          setError(err.response?.data?.error || 'Payment capture failed.');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, []);

  // Scroll to pricing section when hash is #pricing
  useEffect(() => {
    if (!loading && location.hash === '#pricing' && pricingRef.current) {
      setTimeout(() => {
        pricingRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [loading, location.hash]);

  const handleUpgrade = async (plan, paymentMethod = 'stripe') => {
    try {
      setLoading(true);
      const userEmail = user?.email || localStorage.getItem('halisoft_email');
      if (!userEmail) {
        alert('Please log in to upgrade');
        return;
      }

      const dbPlanId = plan?.id ?? plan;
      const planSlug = typeof plan === 'object' ? (plan?.slug || plan?.name?.toLowerCase()) : '';
      const planPrice = typeof plan === 'object' ? Number(plan?.price ?? plan?.base_price) : null;
      const subscribeModule = moduleId || 'global';

      const isFree = planPrice === 0 || planSlug === 'free' || dbPlanId === 'free';
      const isEnterprise = planPrice == null || planSlug === 'enterprise';

      if (isEnterprise) {
        window.location.href = 'mailto:sales@halisoft.biz?subject=Enterprise Plan Inquiry';
        return;
      }

      if (isFree) {
        const response = await api.post('/api/subscriptions/subscribe-free', {
          email: userEmail,
          module: subscribeModule,
          planId: `${subscribeModule}-free`,
          planName: 'Free',
          quota: 100
        });
        if (response.data?.success) {
          alert('✅ ' + (response.data?.message || 'Successfully subscribed to Free!'));
          await loadData();
        } else {
          alert('❌ ' + (response.data?.error || 'Failed to subscribe'));
        }
        return;
      }

      // Paid plan: use Stripe or PayPal (same flow as main PricingPage)
      if (paymentMethod === 'stripe') {
        const response = await api.post('/api/subscriptions/stripe/create-checkout', {
          planId: dbPlanId,
          email: userEmail,
          module: subscribeModule
        });
        if (response.data?.url) {
          window.location.href = response.data.url;
          return;
        }
        throw new Error(response.data?.error || 'No Stripe URL received');
      }

      const response = await api.post('/api/subscriptions/paypal/create-order', {
        planId: dbPlanId,
        email: userEmail,
        module: subscribeModule
      });
      if (response.data?.approvalUrl) {
        window.location.href = response.data.approvalUrl;
        return;
      }
      throw new Error(response.data?.error || 'No PayPal URL received');
    } catch (err) {
      console.error('Error upgrading:', err);
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      if (String(msg).toLowerCase().includes('not configured')) {
        alert('Payment gateway is not configured. Please contact sales@halisoft.biz');
      } else {
        alert('❌ Error upgrading: ' + msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCanceling(true);
      const userEmail = user?.email || localStorage.getItem('halisoft_email');
      const cancelModule = moduleId || 'global';
      await api.post('/api/subscriptions/cancel', { email: userEmail, module: cancelModule });

      // Reload data to show updated subscription
      await loadData();
      setShowCancelModal(false);
      alert('✅ Subscription cancelled successfully. You will retain access until the end of the billing period.');
    } catch (err) {
      console.error('Error canceling:', err);
      alert('Error cancelling. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/api/auth/logout');
      localStorage.removeItem('halisoft_user');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  if (loading) {
    return (
      <div className="subscription-loading">
        <div className="spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-error">
        <p>{error}</p>
        <button onClick={loadData} className="btn-retry">
          Retry
        </button>
      </div>
    );
  }

  // Get current subscription - could be subscription object or plan object
  const subscriptionData = user?.subscription?.plan;
  console.log('[MySubscription] subscriptionData:', subscriptionData);
  console.log('[MySubscription] plans:', plans);
  
  // Match by slug or plan_id (subscription data has plan_id, plans have id and slug)
  let currentPlan;
  if (subscriptionData && subscriptionData.plan_id) {
    // We have a subscription - find the matching plan
    const planId = parseInt(subscriptionData.plan_id);
    currentPlan = plans.find(p => p.id === planId || p.slug === subscriptionData.slug);
    if (!currentPlan) {
      // Use subscription data directly as plan info
      currentPlan = {
        id: planId,
        name: subscriptionData.name || 'Unknown',
        slug: subscriptionData.slug,
        price: subscriptionData.price,
        quota: subscriptionData.quota
      };
    }
    console.log('[MySubscription] Found currentPlan from subscription:', currentPlan);
  } else {
    // No subscription - default to free plan
    currentPlan = plans.find(p => parseFloat(p.price) === 0 && p.slug === 'free') || plans[0];
    console.log('[MySubscription] Using default free plan:', currentPlan);
  }
  
  const currentPlanId = subscriptionData?.plan_id ? parseInt(subscriptionData.plan_id) : currentPlan?.id;

  return (
    <div className="my-subscription">
      <div className="subscription-container">
        {/* My Tools Section - Always visible at top */}
        <div className="my-tools-section" style={{ marginBottom: '2rem' }}>
          <div style={{
            background: colors.sectionBg,
            borderRadius: '16px',
            padding: '1.5rem',
            border: isDark ? '1px solid #7c3aed40' : '1px solid #7c3aed30'
          }}>
            <h2 style={{ margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', color: colors.textPrimary }}>
              <span style={{ fontSize: '1.5rem' }}>🛠️</span>
              My AI Tools
            </h2>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
              gap: '1.5rem',
              marginTop: '1rem'
            }}>
              {/* Risk Analysis Tool Card */}
              <div style={{
                background: colors.cardBg,
                borderRadius: '16px',
                padding: '1.5rem',
                border: isDark ? '2px solid #f43f5e50' : '2px solid #f43f5e30',
                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(244,63,94,0.1)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(244,63,94,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(244,63,94,0.1)';
              }}>
                {/* Header with icon and plan badge */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #f43f5e, #e11d48)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem'
                    }}>
                      📊
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: colors.textPrimary }}>HaliCredit</h3>
                      <p style={{ margin: 0, color: colors.textSecondary, fontSize: '0.8rem' }}>Credit Risk Assessment</p>
                    </div>
                  </div>
                  {/* Dynamic Plan Badge - Uses riskData */}
                  {(() => {
                    const planName = riskData.subscription?.name || riskData.quota?.plan || 'Free';
                    const isPro = planName.toLowerCase().includes('pro') || planName.toLowerCase().includes('professional');
                    const isStarter = planName.toLowerCase().includes('starter');
                    
                    let badgeStyle = {
                      background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                      color: '#92400e',
                      border: '1px solid #fcd34d'
                    };
                    
                    if (isPro) {
                      badgeStyle = {
                        background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                        color: 'white',
                        border: 'none'
                      };
                    } else if (isStarter) {
                      badgeStyle = {
                        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                        color: 'white',
                        border: 'none'
                      };
                    }
                    
                    return (
                      <div style={{
                        ...badgeStyle,
                        padding: '0.35rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '700'
                      }}>
                        {planName} Plan
                      </div>
                    );
                  })()}
                </div>

                {/* Usage Stats - Uses riskData.quota */}
                <div style={{
                  background: isDark ? 'rgba(244,63,94,0.15)' : '#fef2f2',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', color: isDark ? '#fca5a5' : '#991b1b' }}>Usage this month</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: isDark ? '#fca5a5' : '#991b1b' }}>
                      {riskData.quota?.used || 0} / {riskData.quota?.limit || 10} analyses
                    </span>
                  </div>
                  <div style={{ height: '6px', background: isDark ? 'rgba(244,63,94,0.3)' : '#fecaca', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(((riskData.quota?.used || 0) / (riskData.quota?.limit || 10)) * 100, 100)}%`,
                      height: '100%',
                      background: '#f43f5e',
                      borderRadius: '3px'
                    }} />
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={() => {
                      const userEmail = user?.email || localStorage.getItem('halisoft_email') || '';
                      navigate(`/risk-analysis${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ''}`);
                    }}
                    style={{
                      flex: 2,
                      padding: '0.75rem 1rem',
                      background: 'linear-gradient(135deg, #f43f5e, #e11d48)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    🚀 Launch Tool
                  </button>
                  <button
                    onClick={() => {
                      const e = user?.email || localStorage.getItem('halisoft_email') || '';
                      navigate(`/subscription?module=risk-analysis${e ? `&email=${encodeURIComponent(e)}` : ''}#pricing`);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: '#fef2f2',
                      color: '#be123c',
                      border: '2px solid #fecaca',
                      borderRadius: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    ⬆️ Upgrade
                  </button>
                </div>
              </div>

              {/* Leads Agent Tool Card */}
              <div style={{
                background: colors.cardBg,
                borderRadius: '16px',
                padding: '1.5rem',
                border: isDark ? '2px solid #8b5cf650' : '2px solid #8b5cf630',
                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(139,92,246,0.1)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 30px rgba(139,92,246,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(139,92,246,0.1)';
              }}>
                {/* Header with icon and plan badge */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem'
                    }}>
                      🎯
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: colors.textPrimary }}>HaliLeads</h3>
                      <p style={{ margin: 0, color: colors.textSecondary, fontSize: '0.8rem' }}>Lead Qualification Agent</p>
                    </div>
                  </div>
                  {/* Dynamic Plan Badge - Uses leadsData */}
                  {(() => {
                    const planName = leadsData.subscription?.name || leadsData.quota?.plan || 'Free';
                    const isPro = planName.toLowerCase().includes('pro') || planName.toLowerCase().includes('professional');
                    const isStarter = planName.toLowerCase().includes('starter');
                    
                    let badgeStyle = {
                      background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                      color: '#92400e',
                      border: '1px solid #fcd34d'
                    };
                    
                    if (isPro) {
                      badgeStyle = {
                        background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                        color: 'white',
                        border: 'none'
                      };
                    } else if (isStarter) {
                      badgeStyle = {
                        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                        color: 'white',
                        border: 'none'
                      };
                    }
                    
                    return (
                      <div style={{
                        ...badgeStyle,
                        padding: '0.35rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '700'
                      }}>
                        {planName} Plan
                      </div>
                    );
                  })()}
                </div>

                {/* Usage Stats */}
                <div style={{
                  background: isDark ? 'rgba(139,92,246,0.15)' : '#f5f3ff',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', color: isDark ? '#c4b5fd' : '#5b21b6' }}>Usage this month</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: isDark ? '#c4b5fd' : '#5b21b6' }}>
                      {leadsData.quota?.used || 0} / {leadsData.quota?.limit || 20} leads
                    </span>
                  </div>
                  <div style={{ height: '6px', background: isDark ? 'rgba(139,92,246,0.3)' : '#ddd6fe', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(((leadsData.quota?.used || 0) / (leadsData.quota?.limit || 20)) * 100, 100)}%`,
                      height: '100%',
                      background: '#8b5cf6',
                      borderRadius: '3px'
                    }} />
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={() => {
                      const userEmail = user?.email || localStorage.getItem('halisoft_email') || '';
                      navigate(`/hali-leads${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ''}`);
                    }}
                    style={{
                      flex: 2,
                      padding: '0.75rem 1rem',
                      background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    🚀 Launch Tool
                  </button>
                  <button
                    onClick={() => {
                      const e = user?.email || localStorage.getItem('halisoft_email') || '';
                      navigate(`/subscription?module=hali-leads${e ? `&email=${encodeURIComponent(e)}` : ''}#pricing`);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: '#f5f3ff',
                      color: '#6d28d9',
                      border: '2px solid #ddd6fe',
                      borderRadius: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    ⬆️ Upgrade
                  </button>
                </div>
              </div>

              {/* HaliContract Card */}
              <div style={{
                background: colors.cardBg,
                borderRadius: '16px',
                padding: '1.25rem',
                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(16,185,129,0.1)',
                border: isDark ? '1px solid #10b98150' : '1px solid #d1fae5',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(16,185,129,0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16,185,129,0.1)';
              }}>
                {/* Header with icon and plan badge */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem'
                    }}>
                      📜
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: colors.textPrimary }}>HaliContract</h3>
                      <p style={{ margin: 0, color: colors.textSecondary, fontSize: '0.8rem' }}>Contract Analyzer Agent</p>
                    </div>
                  </div>
                  {/* Dynamic Plan Badge - Uses contractData */}
                  {(() => {
                    const planName = contractData.subscription?.name || contractData.quota?.plan || 'Free';
                    const isPro = planName.toLowerCase().includes('pro') || planName.toLowerCase().includes('professional');
                    const isStarter = planName.toLowerCase().includes('starter');
                    
                    let badgeStyle = {
                      background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                      color: '#92400e',
                      border: '1px solid #fcd34d'
                    };
                    
                    if (isPro) {
                      badgeStyle = {
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        color: 'white',
                        border: 'none'
                      };
                    } else if (isStarter) {
                      badgeStyle = {
                        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                        color: 'white',
                        border: 'none'
                      };
                    }
                    
                    return (
                      <div style={{
                        ...badgeStyle,
                        padding: '0.35rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '700'
                      }}>
                        {planName} Plan
                      </div>
                    );
                  })()}
                </div>

                {/* Usage Stats */}
                <div style={{
                  background: isDark ? 'rgba(16,185,129,0.15)' : '#ecfdf5',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', color: isDark ? '#6ee7b7' : '#047857' }}>Usage this month</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: isDark ? '#6ee7b7' : '#047857' }}>
                      {contractData.quota?.used || 0} / {contractData.quota?.limit || 5} analyses
                    </span>
                  </div>
                  <div style={{ height: '6px', background: isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(((contractData.quota?.used || 0) / (contractData.quota?.limit || 5)) * 100, 100)}%`,
                      height: '100%',
                      background: '#10b981',
                      borderRadius: '3px'
                    }} />
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={() => {
                      const userEmail = user?.email || localStorage.getItem('halisoft_email') || '';
                      navigate(`/contract-analyzer${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ''}`);
                    }}
                    style={{
                      flex: 2,
                      padding: '0.75rem 1rem',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    🚀 Launch Tool
                  </button>
                  <button
                    onClick={() => {
                      const e = user?.email || localStorage.getItem('halisoft_email') || '';
                      navigate(`/subscription?module=contract-analyzer${e ? `&email=${encodeURIComponent(e)}` : ''}#pricing`);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: '#ecfdf5',
                      color: '#047857',
                      border: '2px solid #a7f3d0',
                      borderRadius: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    ⬆️ Upgrade
                  </button>
                </div>
              </div>

              {/* HaliCheck (Invoice Financing) Card */}
              <div style={{
                background: colors.cardBg,
                borderRadius: '16px',
                padding: '1.25rem',
                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(99,102,241,0.1)',
                border: isDark ? '1px solid #6366f150' : '1px solid #e0e7ff',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(99,102,241,0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(99,102,241,0.1)';
              }}>
                {/* Header with icon and plan badge */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem'
                    }}>
                      📋
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: colors.textPrimary }}>HaliVerify</h3>
                      <p style={{ margin: 0, color: colors.textSecondary, fontSize: '0.8rem' }}>Invoice Eligibility Checker</p>
                    </div>
                  </div>
                  {/* Dynamic Plan Badge - Uses invoiceFinancingData */}
                  {(() => {
                    const planName = invoiceFinancingData.subscription?.name || invoiceFinancingData.quota?.plan || 'Free';
                    const isPro = planName.toLowerCase().includes('pro') || planName.toLowerCase().includes('professional');
                    const isStarter = planName.toLowerCase().includes('starter');

                    let badgeStyle = {
                      background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                      color: '#92400e',
                      border: '1px solid #fcd34d'
                    };

                    if (isPro) {
                      badgeStyle = {
                        background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                        color: 'white',
                        border: 'none'
                      };
                    } else if (isStarter) {
                      badgeStyle = {
                        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                        color: 'white',
                        border: 'none'
                      };
                    }

                    return (
                      <div style={{
                        ...badgeStyle,
                        padding: '0.35rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '700'
                      }}>
                        {planName} Plan
                      </div>
                    );
                  })()}
                </div>

                {/* Usage Stats */}
                <div style={{
                  background: isDark ? 'rgba(99,102,241,0.15)' : '#eef2ff',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', color: isDark ? '#a5b4fc' : '#4338ca' }}>Usage this month</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: isDark ? '#a5b4fc' : '#4338ca' }}>
                      {invoiceFinancingData.quota?.used || 0} / {invoiceFinancingData.quota?.limit || 10} analyses
                    </span>
                  </div>
                  <div style={{ height: '6px', background: isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(((invoiceFinancingData.quota?.used || 0) / (invoiceFinancingData.quota?.limit || 10)) * 100, 100)}%`,
                      height: '100%',
                      background: '#6366f1',
                      borderRadius: '3px'
                    }} />
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={() => {
                      const userEmail = user?.email || localStorage.getItem('halisoft_email') || '';
                      navigate(`/invoice-financing${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ''}`);
                    }}
                    style={{
                      flex: 2,
                      padding: '0.75rem 1rem',
                      background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    🚀 Launch Tool
                  </button>
                  <button
                    onClick={() => {
                      const e = user?.email || localStorage.getItem('halisoft_email') || '';
                      navigate(`/subscription?module=invoice-financing-check${e ? `&email=${encodeURIComponent(e)}` : ''}#pricing`);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: '#eef2ff',
                      color: '#4338ca',
                      border: '2px solid #c7d2fe',
                      borderRadius: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    ⬆️ Upgrade
                  </button>
                </div>
              </div>
            </div>

            {/* HaliTrade Card */}
            <div style={{
              background: colors.cardBg,
              borderRadius: '16px',
              padding: '1.5rem',
              border: `1px solid ${colors.border}`,
              boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'
            }}>
              <div>
                {/* Header with Icon and Plan Badge */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem'
                    }}>
                      📊
                    </div>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: colors.textPrimary }}>HaliTrade</h3>
                      <p style={{ margin: 0, color: colors.textSecondary, fontSize: '0.8rem' }}>Past Trade Studio</p>
                    </div>
                  </div>
                  {/* Dynamic Plan Badge - Uses pastTradeData */}
                  {(() => {
                    const planName = pastTradeData.subscription?.name || pastTradeData.quota?.plan || 'Free';
                    const isPro = planName.toLowerCase().includes('pro') || planName.toLowerCase().includes('professional');
                    const isStarter = planName.toLowerCase().includes('starter');

                    let badgeStyle = {
                      background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                      color: '#92400e',
                      border: '1px solid #fcd34d'
                    };

                    if (isPro) {
                      badgeStyle = {
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        color: 'white',
                        border: 'none'
                      };
                    } else if (isStarter) {
                      badgeStyle = {
                        background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                        color: 'white',
                        border: 'none'
                      };
                    }

                    return (
                      <div style={{
                        ...badgeStyle,
                        padding: '0.35rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '700'
                      }}>
                        {planName} Plan
                      </div>
                    );
                  })()}
                </div>

                {/* Usage Stats */}
                <div style={{
                  background: isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb',
                  borderRadius: '10px',
                  padding: '0.75rem 1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '0.8rem', color: isDark ? '#fcd34d' : '#d97706' }}>Usage this month</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: isDark ? '#fcd34d' : '#d97706' }}>
                      {pastTradeData.quota?.used || 0} / {pastTradeData.quota?.limit || 5} analyses
                    </span>
                  </div>
                  <div style={{ height: '6px', background: isDark ? 'rgba(245,158,11,0.3)' : '#fef3c7', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${Math.min(((pastTradeData.quota?.used || 0) / (pastTradeData.quota?.limit || 5)) * 100, 100)}%`,
                      height: '100%',
                      background: '#f59e0b',
                      borderRadius: '3px'
                    }} />
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button
                    onClick={() => {
                      const userEmail = user?.email || localStorage.getItem('halisoft_email') || '';
                      navigate(`/past-trade-studio${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ''}`);
                    }}
                    style={{
                      flex: 2,
                      padding: '0.75rem 1rem',
                      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontWeight: '700',
                      cursor: 'pointer',
                      fontSize: '0.95rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    🚀 Launch Tool
                  </button>
                  <button
                    onClick={() => {
                      const e = user?.email || localStorage.getItem('halisoft_email') || '';
                      navigate(`/subscription?module=past-trade-studio${e ? `&email=${encodeURIComponent(e)}` : ''}#pricing`);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      background: '#fffbeb',
                      color: '#d97706',
                      border: '2px solid #fcd34d',
                      borderRadius: '10px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    ⬆️ Upgrade
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Module-specific pricing section */}
        {moduleConfig && (
          <div ref={pricingRef} id="pricing" className="module-pricing-section" style={{ marginBottom: '2rem' }}>
            {/* Module Hero Header */}
            <div style={{
              background: isDark
                ? `linear-gradient(135deg, ${moduleConfig.color}30, ${moduleConfig.color}10)`
                : `linear-gradient(135deg, ${moduleConfig.color}20, ${moduleConfig.color}05)`,
              borderRadius: '20px',
              padding: '2rem',
              marginBottom: '2rem',
              border: isDark ? `1px solid ${moduleConfig.color}40` : `1px solid ${moduleConfig.color}20`,
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Decorative background circles */}
              <div style={{
                position: 'absolute',
                top: '-30px',
                right: '-30px',
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                background: `${moduleConfig.color}10`,
                pointerEvents: 'none'
              }} />
              <div style={{
                position: 'absolute',
                bottom: '-20px',
                right: '80px',
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: `${moduleConfig.color}08`,
                pointerEvents: 'none'
              }} />

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1.5rem', position: 'relative', zIndex: 1 }}>
                <div style={{ flex: 1, minWidth: '280px' }}>
                  {/* Module Icon & Title */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{
                      width: '64px',
                      height: '64px',
                      borderRadius: '16px',
                      background: `linear-gradient(135deg, ${moduleConfig.color}, ${moduleConfig.color}cc)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.75rem',
                      boxShadow: `0 8px 20px ${moduleConfig.color}40`
                    }}>
                      {moduleConfig.icon}
                    </div>
                    <div>
                      <h1 style={{
                        fontSize: '1.75rem',
                        fontWeight: '800',
                        margin: 0,
                        color: colors.textPrimary
                      }}>
                        {moduleConfig.name}
                      </h1>
                      <p style={{
                        fontSize: '1rem',
                        color: moduleConfig.color,
                        margin: 0,
                        fontWeight: '600'
                      }}>
                        {moduleConfig.title}
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p style={{
                    color: colors.textSecondary,
                    fontSize: '1rem',
                    marginBottom: '1.25rem',
                    lineHeight: '1.6',
                    maxWidth: '500px'
                  }}>
                    {moduleConfig.description}
                  </p>

                  {/* Features Tags */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {moduleConfig.features.map((f, i) => (
                      <span key={i} style={{
                        background: isDark ? `${moduleConfig.color}20` : `${moduleConfig.color}15`,
                        color: isDark ? '#e0e7ff' : moduleConfig.color,
                        padding: '0.4rem 0.9rem',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        border: isDark ? `1px solid ${moduleConfig.color}30` : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem'
                      }}>
                        <span style={{ color: '#10b981' }}>✓</span> {f}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Launch Button */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'flex-end' }}>
                  <button
                    onClick={() => {
                      const userEmail = user?.email || localStorage.getItem('halisoft_email') || '';
                      if (moduleConfig.isInternal) {
                        navigate(`${moduleConfig.launchPath}${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ''}`);
                      } else {
                        const mainPlatformUrl = getMainPlatformUrl();
                        window.open(`${mainPlatformUrl}${moduleConfig.launchPath}?email=${encodeURIComponent(userEmail)}`, '_blank');
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.9rem 1.75rem',
                      background: `linear-gradient(135deg, ${moduleConfig.color}, ${moduleConfig.color}dd)`,
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '1rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      boxShadow: `0 6px 20px ${moduleConfig.color}50`,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = `0 10px 30px ${moduleConfig.color}60`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = `0 6px 20px ${moduleConfig.color}50`;
                    }}
                  >
                    🚀 {moduleConfig.launchLabel}
                  </button>
                  <span style={{
                    fontSize: '0.8rem',
                    color: colors.textSecondary
                  }}>
                    Open the tool to start analyzing
                  </span>
                </div>
              </div>
            </div>

            <h2 style={{ marginBottom: '1.5rem', color: colors.textPrimary }}>Choose Your {moduleConfig.name} Plan</h2>
            <div className="plans-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
              {/* For module pages, always show DB-backed plans so admin updates are visible */}
              {plans.map((plan, index) => {
                const isCurrentPlan = currentPlan?.id === plan.id || currentPlan?.slug === plan.slug;
                const isPopular = plan.slug === 'starter' || index === 1;
                const isEnterprise = plan.slug === 'enterprise';
                const isFree = parseFloat(plan.price) === 0;

                return (
                  <div
                    key={plan.id}
                    className="module-plan-card"
                    style={{
                      border: isCurrentPlan
                        ? `2px solid ${moduleConfig.color}`
                        : isPopular
                          ? `2px solid ${moduleConfig.color}60`
                          : isDark ? '1px solid #334155' : '1px solid #e5e7eb',
                      borderRadius: '16px',
                      padding: '1.75rem',
                      background: isDark ? '#1e293b' : 'white',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: isPopular
                        ? `0 8px 30px ${moduleConfig.color}20`
                        : isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = isPopular
                        ? `0 12px 40px ${moduleConfig.color}30`
                        : isDark ? '0 12px 30px rgba(0,0,0,0.4)' : '0 12px 30px rgba(0,0,0,0.12)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = isPopular
                        ? `0 8px 30px ${moduleConfig.color}20`
                        : isDark ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.08)';
                    }}
                  >
                    {/* Popular Badge */}
                    {isPopular && !isCurrentPlan && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: `linear-gradient(135deg, ${moduleConfig.color}, ${moduleConfig.color}dd)`,
                        color: 'white',
                        padding: '0.3rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.7rem',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        ⭐ Popular
                      </div>
                    )}

                    {/* Current Plan Badge */}
                    {isCurrentPlan && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        color: 'white',
                        padding: '0.3rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.7rem',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        ✓ Current
                      </div>
                    )}

                    {/* Plan Icon & Name */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '12px',
                        background: isFree
                          ? (isDark ? 'rgba(107,114,128,0.2)' : '#f3f4f6')
                          : isEnterprise
                            ? (isDark ? 'rgba(139,92,246,0.2)' : '#f5f3ff')
                            : `${moduleConfig.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.25rem'
                      }}>
                        {isFree ? '🆓' : isEnterprise ? '🏢' : '⚡'}
                      </div>
                      <div>
                        <h3 style={{
                          fontSize: '1.15rem',
                          fontWeight: '700',
                          margin: 0,
                          color: colors.textPrimary
                        }}>
                          {plan.name}
                        </h3>
                        <p style={{
                          fontSize: '0.8rem',
                          color: colors.textSecondary,
                          margin: 0
                        }}>
                          {isFree ? 'Get started free' : isEnterprise ? 'Custom solution' : 'For growing teams'}
                        </p>
                      </div>
                    </div>

                    {/* Price */}
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
                        {isEnterprise ? (
                          <span style={{ fontSize: '1.75rem', fontWeight: '800', color: colors.textPrimary }}>Custom</span>
                        ) : isFree ? (
                          <span style={{ fontSize: '2rem', fontWeight: '800', color: colors.textPrimary }}>Free</span>
                        ) : (
                          <>
                            <span style={{ fontSize: '1rem', fontWeight: '600', color: colors.textSecondary }}>$</span>
                            <span style={{ fontSize: '2.25rem', fontWeight: '800', color: moduleConfig.color }}>{plan.price}</span>
                            <span style={{ fontSize: '0.9rem', color: colors.textSecondary }}>/month</span>
                          </>
                        )}
                      </div>

                      {/* Setup fee for paid plans */}
                      {!isFree && !isEnterprise && (plan.setup_fee === undefined || plan.setup_fee > 0) && (
                        <div style={{
                          fontSize: '0.75rem',
                          color: isDark ? '#fbbf24' : '#92400e',
                          background: isDark ? 'rgba(251,191,36,0.15)' : 'linear-gradient(135deg, #fef3c7, #fde68a)',
                          padding: '0.35rem 0.6rem',
                          borderRadius: '6px',
                          marginTop: '0.5rem',
                          display: 'inline-block',
                          fontWeight: '600',
                          border: isDark ? '1px solid rgba(251,191,36,0.3)' : '1px solid #fcd34d'
                        }}>
                          💰 + ${(plan.setup_fee !== undefined ? plan.setup_fee : 1000).toLocaleString()} one-time setup
                        </div>
                      )}
                    </div>

                    {/* Quota Highlight */}
                    <div style={{
                      background: isDark ? 'rgba(99,102,241,0.1)' : `${moduleConfig.color}08`,
                      borderRadius: '10px',
                      padding: '0.85rem 1rem',
                      marginBottom: '1rem',
                      border: isDark ? '1px solid rgba(99,102,241,0.2)' : `1px solid ${moduleConfig.color}20`
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.1rem' }}>📊</span>
                        <span style={{ fontWeight: '700', color: moduleConfig.color, fontSize: '1.1rem' }}>
                          {isEnterprise
                            ? 'Unlimited'
                            : plan.quota?.toLocaleString?.() ? plan.quota.toLocaleString() : plan.quota}
                        </span>
                        <span style={{ color: colors.textSecondary, fontSize: '0.9rem' }}>
                          {moduleId === 'hali-leads' ? 'leads' : 'analyses'}/month
                        </span>
                      </div>
                    </div>

                    {/* Features */}
                    <div style={{ marginBottom: '1.25rem' }}>
                      <p style={{
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: colors.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '0.75rem'
                      }}>
                        What's included
                      </p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {plan.features.map((f, i) => (
                          <li key={i} style={{
                            padding: '0.4rem 0',
                            fontSize: '0.85rem',
                            color: colors.textMuted,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem'
                          }}>
                            <span style={{ color: '#10b981', fontSize: '0.9rem' }}>✓</span>
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA Button */}
                    <button
                      onClick={() => isEnterprise ? window.location.href = 'mailto:sales@halisoft.biz' : handleUpgrade(plan)}
                      disabled={isCurrentPlan}
                      style={{
                        width: '100%',
                        padding: '0.85rem',
                        border: isCurrentPlan ? `2px solid ${moduleConfig.color}` : 'none',
                        borderRadius: '10px',
                        background: isCurrentPlan
                          ? 'transparent'
                          : isEnterprise
                            ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                            : isFree
                              ? (isDark ? 'rgba(255,255,255,0.1)' : '#f3f4f6')
                              : `linear-gradient(135deg, ${moduleConfig.color}, ${moduleConfig.color}dd)`,
                        color: isCurrentPlan
                          ? moduleConfig.color
                          : isFree && !isDark
                            ? '#374151'
                            : 'white',
                        fontWeight: '700',
                        fontSize: '0.95rem',
                        cursor: isCurrentPlan ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      {isCurrentPlan ? (
                        <>✓ Current Plan</>
                      ) : isEnterprise ? (
                        <>📞 Contact Sales</>
                      ) : isFree ? (
                        <>Get Started</>
                      ) : (
                        <>⚡ Upgrade Now</>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Help Section - What can I do? */}
        <div style={{
          background: 'linear-gradient(135deg, #f0fdf4, #ecfdf5)',
          border: '1px solid #86efac',
          borderRadius: '12px',
          padding: '1.25rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ margin: '0 0 0.75rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#166534' }}>
            <span>💡</span> Quick Start
          </h3>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#166534' }}>
                <strong>1.</strong> Click <strong>🚀 Launch</strong> on any tool above to start using it
              </p>
              <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#166534' }}>
                <strong>2.</strong> Your usage is tracked automatically
              </p>
              <p style={{ margin: '0', fontSize: '0.9rem', color: '#166534' }}>
                <strong>3.</strong> Upgrade anytime if you need more
              </p>
            </div>
            <div style={{ 
              background: 'white', 
              borderRadius: '10px', 
              padding: '1rem',
              minWidth: '180px',
              textAlign: 'center',
              border: '1px solid #bbf7d0'
            }}>
              <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.8rem', color: '#6b7280' }}>Current Plan</p>
              <p style={{ 
                margin: '0', 
                fontSize: '1.5rem', 
                fontWeight: '700',
                color: currentPlan?.name?.toLowerCase() === 'free' ? '#6b7280' : '#f59e0b'
              }}>
                {currentPlan?.name || 'Free'}
              </p>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                {quota?.remaining || 0} analyses left
              </p>
            </div>
          </div>
        </div>

        {/* Available Plans (hide when viewing a single module to avoid duplicate/confusing pricing blocks) */}
        {!moduleId && (
        <div className="available-plans">
          <h2>⚡ Need More? Upgrade Your Plan</h2>
          <p className="section-subtitle">
            Get more analyses, priority support, and advanced features
          </p>

          <div className="plans-grid">
            {plans.map(plan => {
              const isCurrent = plan.id === currentPlanId || plan.slug === subscriptionData?.slug;
              const planPrice = parseFloat(plan.price) || 0;
              const currentPrice = parseFloat(currentPlan?.price) || 0;
              const isEnterprise = plan.slug === 'enterprise';
              // Enterprise is always an upgrade (contact sales)
              const isUpgrade = isEnterprise || planPrice > currentPrice;
              const isDowngrade = !isEnterprise && planPrice < currentPrice && planPrice >= 0;

              return (
                <div
                  key={plan.id}
                  className={`plan-card ${isCurrent ? 'current' : ''} ${plan.name === 'STARTER' ? 'popular' : ''}`}
                >
                  {plan.name === 'STARTER' && (
                    <div className="popular-badge">Most Popular</div>
                  )}

                  {isCurrent && (
                    <div className="current-badge">Current Plan</div>
                  )}

                  <div className="plan-header">
                    <h3>{plan.name}</h3>
                    <div className="plan-price-large">
                      {plan.slug === 'enterprise' ? (
                        <span className="price">Customised</span>
                      ) : parseFloat(plan.price) === 0 ? (
                        <span className="price">Free</span>
                      ) : (
                        <>
                          <span className="currency">$</span>
                          <span className="price">{plan.price}</span>
                          <span className="period">/month</span>
                        </>
                      )}
                    </div>
                    <p className="plan-quota-text">
                      {plan.quota ? `${plan.quota.toLocaleString()} analyses / month` : 'Custom quota'}
                    </p>
                    {/* One-time setup fee for paid plans */}
                    {parseFloat(plan.price) > 0 && plan.slug !== 'enterprise' && (plan.setup_fee === undefined || plan.setup_fee > 0) && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: '#92400e',
                        background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                        padding: '0.3rem 0.5rem',
                        borderRadius: '6px',
                        marginTop: '0.5rem',
                        display: 'inline-block',
                        fontWeight: '600',
                        border: '1px solid #fcd34d'
                      }}>
                        💰 + ${(plan.setup_fee !== undefined ? plan.setup_fee : 1000).toLocaleString()} USD one-time setup
                      </div>
                    )}
                  </div>

                  <ul className="plan-features">
                    {plan.features?.map((feature, idx) => (
                      <li key={idx}>
                        <span className="check">✓</span>
                        {feature}
                      </li>
                    )) || (
                      <>
                        <li><span className="check">✓</span> {plan.quota ? `${plan.quota.toLocaleString()} analyses / month` : 'Custom quota'}</li>
                        <li><span className="check">✓</span> Service access</li>
                        <li><span className="check">✓</span> Email support</li>
                        {plan.price > 0 && (
                          <>
                            <li><span className="check">✓</span> Detailed reports</li>
                            <li><span className="check">✓</span> Priority support</li>
                          </>
                        )}
                        {plan.price > 10 && (
                          <>
                            <li><span className="check">✓</span> API access</li>
                            <li><span className="check">✓</span> Account manager</li>
                          </>
                        )}
                      </>
                    )}
                  </ul>

                  <button
                    onClick={() => !isCurrent && !isEnterprise && handleUpgrade(plan)}
                    disabled={isCurrent}
                    className={`btn-plan ${
                      isCurrent ? 'current' :
                      isEnterprise ? 'enterprise' :
                      isUpgrade ? 'upgrade' :
                      isDowngrade ? 'downgrade' : ''
                    }`}
                  >
                    {isCurrent ? '✓ Current Plan' :
                     isEnterprise ? '📞 Contact Sales' :
                     isUpgrade ? '⬆️ Upgrade to This Plan' :
                     isDowngrade ? '⬇️ Downgrade' : 'Select'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* FAQ Section */}
        <div className="faq-section">
          <h2>❓ Frequently Asked Questions</h2>

          <div className="faq-grid">
            <div className="faq-item">
              <h4>Can I change my plan anytime?</h4>
              <p>Yes, you can upgrade or downgrade at any time. Changes take effect immediately.</p>
            </div>

            <div className="faq-item">
              <h4>What happens if I exceed my quota?</h4>
              <p>Access to the service will be blocked until the next month. You can upgrade to continue immediately.</p>
            </div>

            <div className="faq-item">
              <h4>How do I cancel my subscription?</h4>
              <p>Click "Cancel Subscription" above. You will retain access until the end of the paid period.</p>
            </div>

            <div className="faq-item">
              <h4>Are payments secure?</h4>
              <p>Yes, all payments are processed by PayPal with bank-level SSL encryption.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>❌ Cancel Subscription?</h3>
            <p>
              Are you sure you want to cancel your {currentPlan?.name} subscription?
            </p>
            <p className="modal-info">
              You will retain access to your current plan until the end of the billing period.
              After that, you will be automatically downgraded to the FREE plan.
            </p>

            <div className="modal-actions">
              <button
                onClick={() => setShowCancelModal(false)}
                className="btn-secondary"
                disabled={canceling}
              >
                Keep My Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                className="btn-danger"
                disabled={canceling}
              >
                {canceling ? (
                  <>
                    <span className="spinner-small"></span>
                    Cancelling...
                  </>
                ) : (
                  'Confirm Cancellation'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MySubscription;
